/**
 * Client-side search for a blog page. Fetches every page of the blog (within the current tag
 * filter) once via the Section Rendering API, then filters the article cards by their
 * data-search text (title + related product SKUs).
 */
if (!customElements.get('blog-search')) {
  /**
   * Lowercases and strips everything but letters and numbers, so "ab-123" matches "AB123".
   * @param {string} text - Text to squash.
   * @returns {string}
   */
  const squash = (text) => text.toLowerCase().replace(/[^a-z0-9]/g, '');

  class BlogSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-blog-search-input');
      this.status = this.querySelector('.js-blog-search-status');
      this.results = this.querySelector('.js-blog-search-results');
      this.grid = this.parentElement.querySelector('custom-pagination');
      this.searchCount = 0;

      this.input.addEventListener('input', this.handleInput.bind(this));
      // Start loading articles as soon as the visitor focuses the input
      this.input.addEventListener('focus', () => this.getArticles().catch(() => {}), { once: true });
    }

    /**
     * Debounces typing in the search input
     */
    handleInput() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.search(this.input.value), 200);
    }

    /**
     * Fetches every page of results once and caches the article cards
     * @returns {Promise<Array>} Articles with their element and search text.
     */
    getArticles() {
      if (!this.articlesPromise) {
        const totalPages = Number(this.grid.dataset.totalPages) || 1;
        const requests = [];

        for (let page = 1; page <= totalPages; page += 1) {
          const url = `${window.location.pathname}?section_id=${this.dataset.sectionId}&page=${page}`;
          requests.push(
            fetch(url).then((response) => {
              if (!response.ok) throw new Error(response.status);
              return response.text();
            })
          );
        }

        this.articlesPromise = Promise.all(requests).then((pages) => pages.flatMap((html) => {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = html;
          return Array.from(tmpl.content.querySelectorAll('[data-search]'), (el) => ({
            el,
            text: el.dataset.search,
            squashed: squash(el.dataset.search)
          }));
        }));

        // Allow a retry on the next search if loading failed
        this.articlesPromise.catch(() => {
          this.articlesPromise = null;
        });
      }

      return this.articlesPromise;
    }

    /**
     * Shows the cards matching every word of the search, or the normal grid if it's empty
     * @param {string} value - Search input value.
     */
    async search(value) {
      this.searchCount += 1;
      const searchId = this.searchCount;
      const terms = value.toLowerCase().trim().split(/\s+/).filter(Boolean);

      if (!terms.length) {
        this.showGrid();
        return;
      }

      this.setStatus(this.dataset.loadingText);

      let articles;
      try {
        articles = await this.getArticles();
      } catch (error) {
        console.log('Unable to load blog articles for search', error); // eslint-disable-line
        this.showGrid();
        return;
      }

      // A newer search has started since this one
      if (searchId !== this.searchCount) return;

      const matches = articles.filter((article) => terms.every((term) => (
        article.text.includes(term) || (squash(term) && article.squashed.includes(squash(term)))
      )));

      this.results.replaceChildren(...matches.map((article) => article.el.cloneNode(true)));
      this.setStatus(matches.length ? '' : this.dataset.noResultsText.replace('[terms]', value.trim()));
      this.results.hidden = false;
      this.grid.hidden = true;
      this.grid.dataset.pauseInfiniteScroll = 'true';
    }

    /**
     * Hides the search results and restores the normal paginated grid
     */
    showGrid() {
      this.setStatus('');
      this.results.hidden = true;
      this.results.replaceChildren();
      this.grid.hidden = false;
      this.grid.dataset.pauseInfiniteScroll = 'false';
    }

    /**
     * Updates the status message
     * @param {string} text - Message to show, or an empty string to hide it.
     */
    setStatus(text) {
      this.status.textContent = text;
      this.status.hidden = !text;
    }
  }

  customElements.define('blog-search', BlogSearch);
}
